import { useState } from 'react';
import { Spinner } from '@klaro7/ui';

import { useCallHistory } from '../hooks/useCallHistory';
import { callStatusInfo } from '../lib/callStatusInfo';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export function CallHistory() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCallHistory({ page, limit: 20 });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }

  const sessions = data?.data || [];

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 rounded-full bg-neutral-100 p-4">
          <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
          </svg>
        </div>
        <p className="font-medium text-neutral-700">Nessuna chiamata</p>
        <p className="mt-1 text-sm text-neutral-600">Le tue chiamate appariranno qui</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-neutral-100">
      {sessions.map((session) => {
        const statusInfo = callStatusInfo(session);

        return (
          <div key={session.id} className="flex items-center gap-4 px-5 py-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
              session.callType === 'video' ? 'bg-brand-teal-50 text-brand-teal' : 'bg-blue-50 text-blue-600'
            }`}>
              {session.callType === 'video' ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className="font-medium text-neutral-900">
                {session.callType === 'video' ? 'Videochiamata' : 'Chiamata audio'}
              </p>
              <p className="text-sm text-neutral-600">
                {new Date(session.initiatedAt).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>

            <div className="text-right">
              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
              {session.durationSeconds > 0 && (
                <p className="mt-1 text-sm text-neutral-600">{formatDuration(session.durationSeconds)}</p>
              )}
              {session.proEarning != null && session.proEarning > 0 ? (
                <p className="text-sm font-medium text-neutral-900">{session.proEarning.toFixed(2)} €</p>
              ) : (
                session.proEarning === undefined &&
                session.totalCost > 0 && (
                  <p className="text-sm font-medium text-neutral-900">{session.totalCost.toFixed(2)} €</p>
                )
              )}
            </div>
          </div>
        );
      })}

      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3">
          <p className="text-sm text-neutral-600">
            Pagina {data.pagination.page} di {data.pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data.pagination.hasPrevPage}
              className="rounded-lg border border-neutral-200 px-3 py-1 text-sm disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data.pagination.hasNextPage}
              className="rounded-lg border border-neutral-200 px-3 py-1 text-sm disabled:opacity-50"
            >
              Successiva
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
